// Public schedules + standings — public-readable, mobile-responsive.
import { Fragment, useMemo } from 'react';
import type { CSSProperties, Dispatch } from 'react';
import type { Action, AppRole, AppState, GroupLetter, Match, MatchTeamStatus, NavigateFn } from '../types';
import {
  TEAMS, teamById,
} from '../mock/data';
import { computeStandings3v3, computeStandings1v1, computeStandingsEC } from '../state/standings';
import { SWISS_TOTAL_ROUNDS, totalSwissMatches } from '../state/swiss';
import {
  DEFAULT_ARC_SCHEDULE, breakBetween, dayKey, displayTimeFor, formatScheduledAt,
  formatScheduledDay, groupOfMatch, groupsInMatches, resolveBlocks,
} from '../state/schedule';
import { inspectionProgressFor, teamHasInspectionProgress } from '../state/inspection';
import { applyStatusEdit } from '../state/statusTransition';
import { CollapsibleMatchNotes } from '../components/MatchNotes';
import {
  ProtoStatusPill, ProtoEditableStatusPill, EDITABLE_STATUSES, VOL_EDITABLE_STATUSES,
  ProtoTeamChip, ProtoMatchCode, ProtoTopbar, ProtoClockChip, EventTabs, GroupFilter,
} from '../components';
import { useIsMobile } from '../lib/useIsMobile';
import { usePersistentState } from '../lib/usePersistentState';
import { MobilePublicSchedule } from './mobile/MobilePublicSchedule';
import { MobileARCTIC } from './mobile/MobileARCTIC';
import { MobileEC } from './mobile/MobileEC';
import { MobileTopbar } from '../components/MobileTopbar';
import { MobileStandings3v3, MobileStandings1v1, MobileStandingsEC } from './mobile/MobileStandings';

const COMPLETED_LIKE: Match['redS'][] = ['completed', 'forfeit_v', 'forfeit_iv'];
const isLiveOrUpcoming = (m: Match) =>
  m.live || m.onDeck || !(COMPLETED_LIKE.includes(m.redS) && COMPLETED_LIKE.includes(m.blueS));

export function ProtoPublicSchedule({
  event, state, onNavigate, onMenu, role, dispatch,
}: {
  event: '3v3' | '1v1';
  state: AppState;
  onNavigate?: NavigateFn;
  onMenu?: () => void;
  /** Role gates the status-pill editor. Without `role` + `dispatch`, the
   * pill is read-only (the public-facing default). Ops gets a dropdown
   * to set lifecycle status directly from the schedule. */
  role?: AppRole;
  dispatch?: Dispatch<Action>;
}) {
  const isMobile = useIsMobile();
  // 3v3 group filter. Lifted to the wrapper (called unconditionally, before the
  // mobile branch) and persisted, so the choice survives a desktop⇄mobile resize
  // and navigating away + back. 1v1 (Swiss, no groups) leaves it dormant.
  const [groupFilter, setGroupFilter] = usePersistentState<GroupLetter | 'all'>('schedule3v3.group', 'all');
  if (isMobile) {
    return (
      <MobilePublicSchedule
        event={event}
        state={state}
        onNavigate={onNavigate}
        onMenu={onMenu ?? (() => {})}
        role={role}
        dispatch={dispatch}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
      />
    );
  }
  const isOps = role === 'ops';
  const isVolPlus = role === 'volunteer' || role === 'staff' || isOps;
  // Status-edit handler. Ops gets the full editable set (through l2 — see
  // EDITABLE_STATUSES; in_game/completed are NOT pill-settable, they go via
  // advance / mark-completed). Volunteer + staff get a narrowed dropdown
  // (not_tracked ↔ at_inspection) for the pre-queue-to-inspection workflow.
  // Mirrors MatchControl's editStatus helper so the schedule and
  // Match Control share a code path through SET_MATCH_STATUS →
  // reducer → store.
  const editStatus = (m: Match, side: 'red' | 'blue') => (status: MatchTeamStatus) => {
    if (dispatch) applyStatusEdit(dispatch, m, side, status, event, isOps);
  };
  // Per-row gating: volunteers can flip untracked ↔ at_inspection, but
  // ONLY while no robot for that team in that match has cleared S1.
  // Once a robot is past S1, reverting to not_tracked would orphan that
  // progress and pull the team off the inspection desk. Staff + ops
  // bypass the lockdown — trusted to override when something needs
  // unwinding.
  const canVolEditSide = (matchId: string, teamId: string, currentStatus: MatchTeamStatus): boolean => {
    if (!isVolPlus) return false;
    if (role !== 'volunteer') return true;
    if (currentStatus !== 'at_inspection') return true;
    return !teamHasInspectionProgress(teamId, matchId, state);
  };
  // Dropdown options: ops gets the full lifecycle set (through l2); volunteer
  // + staff share the narrowed not_tracked ↔ at_inspection list. Nobody can
  // set in_game / completed from a pill — those aren't in EDITABLE_STATUSES.
  const editableStatusesForRole = isOps ? EDITABLE_STATUSES : VOL_EDITABLE_STATUSES;
  // 3v3 group filter. Chips reflect the groups actually present; the control
  // only shows when there's more than one to choose between. `all` stays
  // UNFILTERED (renderTime needs the full schedule for block-relative delays) —
  // we narrow only the displayed rows + counts via inActiveGroup.
  const activeGroup = event === '3v3' ? groupFilter : 'all';
  const availableGroups = event === '3v3' ? groupsInMatches(state.matches3v3, state.teamConfig) : [];
  const showGroupFilter = availableGroups.length > 1;
  const inActiveGroup = (m: Match) => activeGroup === 'all' || groupOfMatch(m, state.teamConfig) === activeGroup;

  const all = event === '3v3' ? state.matches3v3 : state.matches1v1;
  const liveOrUpcoming = all.filter(isLiveOrUpcoming).filter(inActiveGroup);
  // Byes are auto-completed, so isLiveOrUpcoming drops them — but a byed team
  // should still see their bye. Surface byes from the current/upcoming rounds
  // (at or after the earliest still-to-play match, the "frontier") so past-round
  // byes don't clutter the list, and sort so each slots in beside its round (a
  // bye shares its round's last real slot).
  const byeFrontier = liveOrUpcoming.map((m) => m.scheduledAt).filter((s): s is string => !!s).sort()[0];
  const matches = [
    ...liveOrUpcoming,
    ...all.filter((m) => m.bye && inActiveGroup(m) && m.scheduledAt && byeFrontier !== undefined && m.scheduledAt >= byeFrontier),
  ].sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
  // 1v1 runs a fixed 6-round Swiss; later rounds aren't generated yet, so the
  // scheduled rows undercount. Report the true remaining count from the field
  // size (byes excluded — they aren't matches to watch). 3v3 is fully scheduled.
  const concludedReal = all.filter((m) => !m.bye && !isLiveOrUpcoming(m)).length;
  const n1v1 = TEAMS.filter((t) => t.events.includes('1v1')).length;
  const upcomingCount = event === '1v1'
    ? Math.max(0, totalSwissMatches(n1v1) - concludedReal)
    : matches.length;
  // Completed tracks the active group when filtered (3v3 has no byes, so this
  // equals all−upcoming under "All"); 1v1 has no groups → the Swiss count.
  const completedCount = event === '1v1'
    ? concludedReal
    : all.filter((m) => inActiveGroup(m) && !m.bye && !isLiveOrUpcoming(m)).length;
  const live = matches.find((m) => m.live);
  const onDeck = matches.find((m) => m.onDeck);

  // Block-aware delay display: matches in the current block (containing
  // live / on-deck) get the active delay applied; later blocks render
  // nominal time (post-break recovery).
  const scheduleBlocks = useMemo(() => resolveBlocks(DEFAULT_ARC_SCHEDULE), []);
  const delay = state.officialDelay[event] ?? 0;
  const delayAnchor = state.officialDelayBlock[event] ?? null;
  const renderTime = (m: Match): { time: string; delayed: boolean } => {
    const r = displayTimeFor(m, all, scheduleBlocks, delay, delayAnchor);
    return {
      time: r.effective ? formatScheduledAt(r.effective) : m.time,
      delayed: r.delayed,
    };
  };

  // Group the upcoming list under a day header so the date is ALWAYS visible —
  // even when every remaining match is on one day (e.g. 1v1 before its later
  // Swiss rounds are generated, which otherwise showed a time with no date).
  // Multi-day spans still get one header per day.
  const upcomingDays = Array.from(new Set(matches.map((m) => dayKey(m.scheduledAt)).filter(Boolean)));
  const showUpcomingDayMarkers = upcomingDays.length > 0;

  return (
    <div className="col full-h min-w-0">
      <ProtoTopbar
        title={`${event.toUpperCase()} LIVE SCHEDULE`}
        subtitle={`${event === '3v3' ? 'NORTH FIELD' : 'SOUTH FIELD'} · ${upcomingCount} UPCOMING · ${completedCount} COMPLETED`}
        right={<ProtoClockChip delayMin={state.officialDelay[event] ?? 0} event={event} hideTime />}
      />
      <div className="public-now-grid p-5 gap-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        {live && <ProtoNowCard match={live} event={event} state={state} label="ON THE FIELD" live timeText={renderTime(live).time} />}
        {onDeck && <ProtoNowCard match={onDeck} event={event} state={state} label="UP NEXT" timeText={renderTime(onDeck).time} />}
      </div>
      <div className="grow overflow-hidden" style={{ padding: '0 18px 18px' }}>
        <div className="card full-h col">
          <div className="card-header">
            <div className="row-base gap-3">
              <span className="card-title">UPCOMING</span>
              {showGroupFilter && (
                <>
                  <span className="mono txt-10 text-3 tracking-2">GROUP</span>
                  <GroupFilter value={groupFilter} onChange={setGroupFilter} groups={availableGroups} />
                </>
              )}
            </div>
            <span className="mono txt-11 text-2">
              {completedCount} COMPLETED · SEE STANDINGS FOR RESULTS
            </span>
          </div>
          <div className="lg-table-wrap overflow-auto">
            <table className="lg-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Match</th>
                  <th style={{ width: 80 }}>Time</th>
                  <th>Red</th>
                  <th style={{ width: 180 }}>Red Status</th>
                  <th>Blue</th>
                  <th style={{ width: 180 }}>Blue Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let lastDay = '';
                  let prevMatch: Match | null = null;
                  return matches.map((m) => {
                    const today = dayKey(m.scheduledAt);
                    const dayChanged = showUpcomingDayMarkers && today && today !== lastDay;
                    if (dayChanged) lastDay = today;
                    // Inter-block break (lunch / dinner) within the same day:
                    // suppress when day already changed (day marker covers it).
                    const brk = !dayChanged ? breakBetween(prevMatch, m, scheduleBlocks) : null;
                    prevMatch = m;
                    const tdisp = renderTime(m);
                    return (
                      <Fragment key={m.id}>
                        {dayChanged && (
                          <tr>
                            <td colSpan={6} className="mono txt-10 fw-bold text-2 tracking-4 bg-3 border-t pad-cell" style={{ padding: '8px 14px' }}>
                              {formatScheduledDay(m.scheduledAt).toUpperCase()}
                            </td>
                          </tr>
                        )}
                        {brk && (
                          <tr>
                            <td colSpan={6} className="mono txt-10 fw-bold text-2 tracking-4 text-center bg-transparent" style={{
                              padding: '8px 14px',
                              borderTop: '1px dashed var(--line)',
                              borderBottom: '1px dashed var(--line)',
                            }}>
                              ── {brk.label.toUpperCase()} BREAK ·
                              {' '}{formatScheduledAt(brk.starts)} – {formatScheduledAt(brk.ends)} ──
                            </td>
                          </tr>
                        )}
                        {m.bye ? (
                          <tr>
                            <td><ProtoMatchCode code={m.id} /></td>
                            <td className="mono tnum txt-12 text-1">{tdisp.time}</td>
                            <td><ProtoTeamChip id={m.red} side="red" logo size={26} onClick={onNavigate ? () => onNavigate('teams', { team: m.red }) : undefined} /></td>
                            <td colSpan={3} className="mono txt-11 text-3 tracking-2">BYE · sits out this round (counts as a win)</td>
                          </tr>
                        ) : (
                        <tr className={m.live ? 'live-row' : ''}>
                          <td><ProtoMatchCode code={m.id} live={m.live} onDeck={m.onDeck} /></td>
                          <td
                            className={`mono tnum txt-12 ${tdisp.delayed ? 'text-warn' : 'text-1'}`}
                            title={tdisp.delayed ? `Delayed +${delay}m from ${m.time}` : undefined}
                          >
                            {tdisp.time}
                          </td>
                          <td>
                            <ProtoTeamChip id={m.red} side="red" logo size={26} onClick={onNavigate ? () => onNavigate('teams', { team: m.red }) : undefined} />
                          </td>
                          <td>
                            <ProtoEditableStatusPill
                              status={m.redS}
                              onChange={canVolEditSide(m.id, m.red, m.redS) ? editStatus(m, 'red') : undefined}
                              editableStatuses={editableStatusesForRole}
                              inspection={inspectionProgressFor(m.red, m.id, event, state)}
                            />
                          </td>
                          <td>
                            <ProtoTeamChip id={m.blue} side="blue" logo size={26} onClick={onNavigate ? () => onNavigate('teams', { team: m.blue }) : undefined} />
                          </td>
                          <td>
                            <ProtoEditableStatusPill
                              status={m.blueS}
                              onChange={canVolEditSide(m.id, m.blue, m.blueS) ? editStatus(m, 'blue') : undefined}
                              editableStatuses={editableStatusesForRole}
                              inspection={inspectionProgressFor(m.blue, m.id, event, state)}
                            />
                          </td>
                        </tr>
                        )}
                        {isVolPlus && dispatch && !m.bye && (
                          <tr>
                            <td colSpan={6} style={{ padding: '0 14px 10px' }}>
                              <CollapsibleMatchNotes
                                notes={state.matchNotes[m.id] ?? []}
                                canWrite
                                onAppend={(body) => dispatch({ type: 'APPEND_MATCH_NOTE', matchId: m.id, body })}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  });
                })()}
                {matches.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-2 p-7">
                      {completedCount === 0
                        ? "Schedule will be released after the Captain's Meeting."
                        : 'All scheduled matches complete.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProtoNowCard({ match, event, state, label, live, timeText }: {
  match: Match;
  event: '3v3' | '1v1';
  state: AppState;
  label: string;
  live?: boolean;
  /** Delay-adjusted display time (from the parent's renderTime); falls back to
   *  the raw match.time so the card matches the delayed schedule list below. */
  timeText?: string;
}) {
  const r = teamById(match.red);
  const b = teamById(match.blue);
  // Live-card chrome (gradient + accent border + 2px accent rail) is dynamic
  // and thin enough that it stays inline. Layout + spacing move to utilities.
  return (
    <div
      className="relative overflow-hidden p-4"
      style={{
        background: live ? 'linear-gradient(135deg, var(--bg-1), rgba(255,214,10,0.04))' : 'var(--bg-1)',
        border: 'var(--bw) solid ' + (live ? 'rgba(255,214,10,0.4)' : 'var(--line)'),
        borderRadius: 12,
      }}
    >
      {live && <div className="bg-accent" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
      <div className="row between mb-3">
        <span className={`section-label ${live ? 'text-accent' : 'text-2'}`}>{label}</span>
        <div className="row gap-2">
          <ProtoMatchCode code={match.id} live={live} />
          <span className="mono txt-11 text-2">{timeText ?? match.time}</span>
        </div>
      </div>
      <div className="now-card-grid gap-4" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
        <div className="min-w-0" style={{ borderLeft: '4px solid var(--red-team)', paddingLeft: 10 }}>
          <div className="section-label text-red txt-9">RED</div>
          <div className="display txt-26 truncate mt-1" style={{ lineHeight: 1.05 }}>{r.short}</div>
          <div className="mt-2">
            <ProtoStatusPill status={match.redS} inspection={inspectionProgressFor(match.red, match.id, event, state)} />
          </div>
        </div>
        <div className="display now-card-vs txt-28 text-3">VS</div>
        <div className="now-card-blue-side text-right min-w-0" style={{ borderRight: '4px solid var(--blue-team)', paddingRight: 10 }}>
          <div className="section-label text-blue txt-9">BLUE</div>
          <div className="display txt-26 truncate mt-1" style={{ lineHeight: 1.05 }}>{b.short}</div>
          <div className="row end mt-2">
            <ProtoStatusPill status={match.blueS} inspection={inspectionProgressFor(match.blue, match.id, event, state)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProtoARCTIC({ state, onMenu, onNavigate }: { state: AppState; onMenu?: () => void; onNavigate?: NavigateFn }) {
  const isMobile = useIsMobile();
  const arctic = state.arctic;
  if (isMobile) {
    return <MobileARCTIC entries={arctic} onMenu={onMenu ?? (() => {})} onNavigate={onNavigate} />;
  }
  return (
    <div className="col full-h">
      {/* ARCTIC has no event-coupled delay — show time only. */}
      <ProtoTopbar title="ARCTIC PRESENTATIONS" subtitle={`ROOM 3 · ${arctic.length} SCHEDULED`} right={<ProtoClockChip hideTime />} />
      <div className="grow overflow-auto p-5">
        <div className="card">
          <table className="lg-table">
            <thead><tr><th style={{ width: 64 }}>Day</th><th style={{ width: 90 }}>Time</th><th>Topic</th><th style={{ width: '100%' }}>Authors</th><th>Team</th></tr></thead>
            <tbody>
              {arctic.map((a) => (
                <tr key={a.id}>
                  <td className="mono txt-12 text-2">{a.day}</td>
                  <td className="mono txt-12">{a.time}</td>
                  <td className="fw-semi" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{a.topic}</td>
                  <td className="text-1">{a.presenter}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <ProtoTeamChip
                      id={a.team}
                      logo
                      size={26}
                      onClick={onNavigate ? () => onNavigate('teams', { team: a.team }) : undefined}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ProtoEC({ state, onMenu, onNavigate, role, dispatch }: {
  state: AppState;
  onMenu?: () => void;
  onNavigate?: NavigateFn;
  role?: AppRole;
  dispatch?: Dispatch<Action>;
}) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <MobileEC state={state} onMenu={onMenu ?? (() => {})} onNavigate={onNavigate} role={role} dispatch={dispatch} />;
  }
  const isOps = role === 'ops';
  const isVolPlus = role === 'volunteer' || role === 'staff' || isOps;
  // Only ops gets the full set; staff + vol share the narrowed list.
  const editableStatusesForRole = isOps ? EDITABLE_STATUSES : VOL_EDITABLE_STATUSES;
  // Same SET_MATCH_STATUS path Match Control uses — keeps the schedule
  // and Match Control in sync through the reducer.
  const editECStatus = (matchId: string) => (status: MatchTeamStatus) => {
    dispatch?.({ type: 'SET_MATCH_STATUS', event: 'ec', matchId, side: 'red', status });
  };
  // Same gate as the 3v3/1v1 schedule: volunteers can't revert
  // at_inspection → not_tracked once the engineer robot has cleared S1.
  // Staff + ops bypass the lockdown.
  const canVolEditEC = (matchId: string, teamId: string, currentStatus: MatchTeamStatus): boolean => {
    if (!isVolPlus) return false;
    if (role !== 'volunteer') return true;
    if (currentStatus !== 'at_inspection') return true;
    return !teamHasInspectionProgress(teamId, matchId, state);
  };
  return (
    <div className="col full-h">
      {/* EC runs on the 1v1 field — its delay tracks the 1v1 clock — but
       *   the chip drops the 1V1 tag so spectators on the EC page don't
       *   see "1V1 ON SCHEDULE" floating without context. */}
      <ProtoTopbar title="ENGINEER CHALLENGE" subtitle={`JUL 4 · 1V1 FIELD · ${state.ec.length} RUNS`} right={<ProtoClockChip delayMin={state.officialDelay['1v1'] ?? 0} hideTime />} />
      <div className="grow overflow-auto p-5">
        <div className="card">
          <table className="lg-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Run</th>
                <th style={{ width: 64 }}>Day</th>
                <th style={{ width: 90 }}>Time</th>
                <th>Team</th>
                {/* Penalty column is read-only on the schedule for every role;
                    edits live on the Check-In page (ops only). Visible to all
                    so the value is consistent everywhere it appears. */}
                <th style={{ width: 110, textAlign: 'center' }}>Penalty</th>
                <th style={{ width: 200 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {state.ec.map((e) => {
                // Engineer-robot inspection-progress for the X/Y count
                // on the status pill — same surface as 3v3/1v1.
                const engineerLevel = state.teamRobotInspection[e.team]?.['ec']?.['Engineer'] ?? 'pending';
                const inspection = { passed: engineerLevel === 'l3' ? 1 : 0, total: 1 };
                // Read from state.ecPenalty (sourced from the public
                // `ec_penalties` view) rather than state.checkIn — public
                // viewers can't read check_in via RLS, so this is the only
                // path that reliably surfaces the value to every role.
                const ecPenalty = state.ecPenalty[e.team] ?? 0;
                return (
                  <tr key={e.id} className={e.live ? 'live-row' : ''}>
                    <td><ProtoMatchCode code={e.id} live={e.live} /></td>
                    <td className="mono txt-12 text-2">{e.day}</td>
                    <td className="mono txt-12">{e.time}</td>
                    <td>
                      <ProtoTeamChip
                        id={e.team}
                        full
                        logo
                        size={26}
                        onClick={onNavigate ? () => onNavigate('teams', { team: e.team }) : undefined}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <ECPenaltyPill value={ecPenalty} />
                    </td>
                    <td>
                      <ProtoEditableStatusPill
                        status={e.status}
                        onChange={canVolEditEC(e.id, e.team, e.status) ? editECStatus(e.id) : undefined}
                        editableStatuses={editableStatusesForRole}
                        inspection={inspection}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Read-only EC penalty display. Renders a small red pill ("Ns" — the
// value is in seconds added to the engineer's run time) when the team
// has a non-zero penalty noted at check-in; renders an em-dash
// placeholder otherwise. Visible to every role including public (the
// value is sourced from the public-readable `ec_penalties` view).
// Edits happen on the Check-In page (ops only).
function ECPenaltyPill({ value }: { value: number }) {
  if (value <= 0) {
    return <span className="mono txt-11 text-3 tracking-2">—</span>;
  }
  return (
    <span
      className="mono txt-11 fw-bold tracking-2"
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 4,
        color: 'var(--err)',
        background: 'var(--err-bg)',
        border: 'var(--bw) solid rgba(255,71,87,0.4)',
      }}
      title="Advisory penalty (seconds) noted at check-in"
    >
      {value}s
    </span>
  );
}

export function ProtoStandings({ state, onNavigate, onMenu }: {
  state: AppState; onNavigate?: NavigateFn; onMenu?: () => void;
}) {
  const isMobile = useIsMobile();
  // Live standings derived from match outcomes. computeStandings3v3 reads
  // matches3v3 + matchResults; computeStandings1v1 the same for 1v1.
  // computeStandingsEC reads state.ec + matchResults + robotWeight (the
  // weight tiebreak comes from S1 inspection). The bracket screen
  // consumes computeTopEight3v3 separately.
  // Tab state persisted across mounts so navigating away (e.g., into a
  // team detail) and back via the team page's back arrow drops the user
  // on the same tab they last selected.
  const [tab, setTab] = usePersistentState<'3v3' | '1v1' | 'ec'>('standings.tab', '3v3');
  // Standings are O(matches × teams) per call; only recompute when their
  // direct inputs change. Avoids re-running on unrelated state mutations
  // (queue reorder, warning add, etc.) — meaningful at 28 teams + 60 quals.
  const groups3v3 = useMemo(
    () => computeStandings3v3(state.matches3v3, state.matchResults, TEAMS, state.checkIn, state.teamConfig),
    [state.matches3v3, state.matchResults, state.checkIn, state.teamConfig],
  );
  const swiss1v1 = useMemo(
    () => computeStandings1v1(state.matches1v1, state.matchResults, TEAMS, SWISS_TOTAL_ROUNDS),
    [state.matches1v1, state.matchResults],
  );
  const ecStandings = useMemo(
    () => computeStandingsEC(state.ec, state.matchResults, state.robotWeight, TEAMS),
    [state.ec, state.matchResults, state.robotWeight],
  );
  const subtitle =
    tab === '3v3' ? '3V3 · GROUP STAGE · 4 GROUPS · TOP 2 PER GROUP ADVANCE' :
    tab === '1v1' ? `1V1 · SWISS · ${SWISS_TOTAL_ROUNDS} ROUNDS · TOP 8 ADVANCE` :
                    'ENGINEER CHALLENGE · 3 ROUNDS';
  // Mobile + desktop share the same tab state. Mobile renders a compact
  // segmented control (mw-seg) and uses the per-event mobile body
  // components; desktop keeps the existing 3-column tables.
  const tabSwitcher = (
    <div className="mw-seg" style={{ display: isMobile ? 'flex' : 'none' }}>
      {(['3v3', '1v1', 'ec'] as const).map((v) => (
        <div key={v} className={tab === v ? 'active' : ''} onClick={() => setTab(v)}>
          {v === 'ec' ? 'ENG' : v.toUpperCase()}
        </div>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <div className="mw-app">
        <MobileTopbar
          title="QUALS STANDINGS"
          subtitle={tab === '3v3'
            ? `${groups3v3.reduce((s, g) => s + g.rows.length, 0)} TEAMS · ${groups3v3.length} GROUPS`
            : tab === '1v1'
              ? `SWISS · R${swiss1v1.round} OF ${swiss1v1.totalRounds}`
              : `${ecStandings.length} TEAMS · BEST 2 OF 3 AVG`}
          onMenu={onMenu ?? (() => {})}
          right={tabSwitcher}
        />
        {tab === '3v3' ? <MobileStandings3v3 groups={groups3v3} onNavigate={onNavigate} /> :
         tab === '1v1' ? <MobileStandings1v1 swiss={swiss1v1} onNavigate={onNavigate} /> :
                         <MobileStandingsEC  rows={ecStandings} onNavigate={onNavigate} />}
      </div>
    );
  }
  return (
    <div className="col full-h">
      <ProtoTopbar
        title="QUALS STANDINGS"
        subtitle={subtitle}
        right={
          <EventTabs value={tab} onChange={setTab} events={['3v3', '1v1', 'ec']} variant="subtle" />
        }
      />
      <div className="grow overflow-auto p-5">
        {tab === '3v3' ? <Standings3v3 groups={groups3v3} onNavigate={onNavigate} /> :
         tab === '1v1' ? <Standings1v1 swiss={swiss1v1} onNavigate={onNavigate} /> :
                          <StandingsEC rows={ecStandings} onNavigate={onNavigate} />}
      </div>
    </div>
  );
}

function Standings3v3({ groups, onNavigate }: { groups: ReturnType<typeof computeStandings3v3>; onNavigate?: NavigateFn }) {
  return (
    <div className="gap-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
      {groups.map((g) => (
        <div key={g.name} className="card">
          <div className="card-header">
            <div className="row-base gap-3">
              <span className="display txt-28 text-accent">GROUP {g.name}</span>
              <span className="mono txt-10 text-2">{g.rows.length} TEAMS</span>
            </div>
            <span className="mono txt-10 text-3">TOP 2 ADVANCE</span>
          </div>
          <table className="lg-table">
            <thead>
              <tr>
                <th style={{ width: 38 }}>#</th>
                <th>Team</th>
                <th className="text-right" style={{ width: 50 }}>Win</th>
                <th className="text-right" style={{ width: 50 }}>Loss</th>
                <th className="text-right" style={{ width: 50 }}>Tie</th>
                <th className="text-right" style={{ width: 64 }}>Penalty</th>
                <th className="text-right" style={{ width: 64 }} title="VP differential: own VP − opponent VP across 3v3 quals. Tiebreaker after head-to-head.">VP Δ</th>
                <th className="text-right" style={{ width: 56 }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => {
                const t = teamById(r.team);
                // Subtle yellow tint marks the top-2 advance rows. Stays
                // inline because the rgba() doesn't have a token yet.
                const rowStyle: CSSProperties | undefined = r.advance ? { background: 'rgba(255,214,10,0.04)' } : undefined;
                return (
                  <tr key={`${r.team}-${r.rank}`} style={rowStyle}>
                    <td>
                      <div className="row gap-2">
                        {r.advance && <span className="bg-accent" style={{ width: 3, height: 18, borderRadius: 2 }} />}
                        <span className="display txt-18">{r.rank}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`team-name txt-14 ${onNavigate ? 'team-name-link' : ''}`}
                        onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                        title={onNavigate ? `Open ${t.name}` : undefined}
                      >{t.short}</span>
                      {r.needsTiebreaker && (
                        <span
                          className="mono ml-1 txt-9 fw-bold tracking-3 border border-warn r-3 bg-warn-soft text-warn"
                          title="Tied with another team after Score / H2H / VP differential — operator should run a BO3 to resolve."
                          style={{ marginLeft: 6, padding: '1px 5px' }}
                        >BO3?</span>
                      )}
                    </td>
                    <td className="mono tnum text-right text-1 fw-bold">{r.w}</td>
                    <td className="mono tnum text-right text-1">{r.l}</td>
                    <td className="mono tnum text-right text-2">{r.t}</td>
                    <td className={`mono tnum text-right ${r.penalty ? 'text-err fw-bold' : 'text-3'}`}>
                      {r.penalty ? `−${r.penalty}` : '—'}
                    </td>
                    {/* VP differential (op − dp): the tiebreaker after head-to-head. */}
                    <td className="mono tnum text-right text-2">{r.op - r.dp >= 0 ? '+' : ''}{r.op - r.dp}</td>
                    {/* Score (RP) drives advancement — green-highlighted, kept as the
                        last column (conventional standings layout: diff then points). */}
                    <td className="mono tnum text-right text-ok fw-bold">{r.rp}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <div className="mono txt-11 text-2 p-1" style={{ gridColumn: 'span 2' }}>
        BO2 SCORE: W+W = 3, W+T = 1, T+T = 0, L+T = 0, L+L = 0 · TIES BROKEN BY HEAD-TO-HEAD → VP DIFFERENTIAL → BO3
      </div>
    </div>
  );
}

function Standings1v1({ swiss, onNavigate }: { swiss: ReturnType<typeof computeStandings1v1>; onNavigate?: NavigateFn }) {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row-base gap-3">
          <span className="card-title">SWISS STANDINGS</span>
          <span className="mono txt-10 text-2">ROUND {swiss.round} OF {swiss.totalRounds}</span>
        </div>
        <span className="mono txt-11 text-2">TOP 8 ADVANCE TO BRACKET</span>
      </div>
      <table className="lg-table">
        <thead>
          <tr>
            <th style={{ width: 50 }}>#</th>
            <th>Team</th>
            <th className="text-right" style={{ width: 60 }}>Win</th>
            <th className="text-right" style={{ width: 60 }}>Loss</th>
            <th className="text-right" style={{ width: 60 }}>Tie</th>
            <th className="text-right" style={{ width: 80 }} title="Tiebreaker 1: sum of the team's running Score total after each round — rewards scoring early.">Cumulative</th>
            <th className="text-right" style={{ width: 76 }} title="Tiebreaker 2 (median Buchholz): strength of schedule — sum of opponents' Scores after dropping the highest and lowest.">Median</th>
            <th className="text-right" style={{ width: 70 }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {swiss.rows.map((r) => {
            const t = teamById(r.team);
            // Subtle yellow tint for top-8 advance rows.
            const rowStyle: CSSProperties | undefined = r.advance ? { background: 'rgba(255,214,10,0.03)' } : undefined;
            return (
              <tr key={`${r.team}-${r.rank}`} style={rowStyle}>
                <td>
                  <div className="row gap-2">
                    {r.advance && <span className="bg-accent" style={{ width: 3, height: 22, borderRadius: 2 }} />}
                    <span className="display txt-22">{r.rank}</span>
                  </div>
                </td>
                <td>
                  <span
                    className={`team-name ${onNavigate ? 'team-name-link' : ''}`}
                    onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                    title={onNavigate ? `Open ${t.name}` : undefined}
                  >{t.short}</span>
                  {r.needsTiebreaker && (
                    <span
                      className="mono ml-1 txt-9 fw-bold tracking-3 border border-warn r-3 bg-warn-soft text-warn"
                      title="Tied with another team after Score / H2H / Cumulative / Median Buchholz — operator should run a BO3 to resolve."
                      style={{ marginLeft: 6, padding: '1px 5px' }}
                    >BO3?</span>
                  )}
                </td>
                <td className="mono tnum text-right text-1 fw-bold">{r.w}</td>
                <td className="mono tnum text-right text-1">{r.l}</td>
                <td className="mono tnum text-right text-2">{r.t}</td>
                {/* Tiebreakers before Score so the public can follow how ties resolve. */}
                <td className="mono tnum text-right text-2">{r.cumulativeScore}</td>
                <td className="mono tnum text-right text-2">{r.medianBuchholz}</td>
                {/* Score (RP) drives top-8 advancement — green-highlighted, kept last. */}
                <td className="mono tnum text-right text-ok fw-bold">{r.rp}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="border-t txt-11 text-2" style={{ padding: '10px 18px' }}>
        <span className="mono">SCORE = SUM OF MATCH POINTS · TIES BROKEN BY HEAD-TO-HEAD → CUMULATIVE → MEDIAN BUCHHOLZ</span>
      </div>
    </div>
  );
}

function StandingsEC({ rows, onNavigate }: {
  rows: ReturnType<typeof computeStandingsEC>;
  onNavigate?: NavigateFn;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">ENGINEER CHALLENGE</span>
        <span className="mono txt-11 text-2">{rows.length} TEAMS</span>
      </div>
      <table className="lg-table">
        <thead>
          <tr>
            <th style={{ width: 50 }}>#</th>
            <th>Team</th>
            <th className="text-right" style={{ width: 80 }}>R1</th>
            <th className="text-right" style={{ width: 80 }}>R2</th>
            <th className="text-right" style={{ width: 80 }}>R3</th>
            <th className="text-right" style={{ width: 100 }}>Weight</th>
            <th className="text-right" style={{ width: 100 }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const t = teamById(r.team);
            // Top score gets the same accent as the other standings tables.
            const isLeader = r.rank === 1 && r.finalScore > 0;
            const rs = r.roundScores;
            // For each round score: bold if it contributed to the final (top 2);
            // muted if dropped (the third-best). Rendered with the index of
            // the LOWEST score being the "dropped" one when there are 3.
            const droppedIdx = rs.length === 3
              ? rs.indexOf(Math.min(...rs))
              : -1;
            return (
              <tr key={r.team} style={isLeader ? { background: 'rgba(255,214,10,0.04)' } : undefined}>
                <td>
                  <div className="row gap-2">
                    {isLeader && <span className="bg-accent" style={{ width: 3, height: 22, borderRadius: 2 }} />}
                    <span className="display txt-22">{r.rank}</span>
                  </div>
                </td>
                <td>
                  <span
                    className={`team-name ${onNavigate ? 'team-name-link' : ''}`}
                    onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                    title={onNavigate ? `Open ${t.name}` : undefined}
                  >{t.short}</span>
                </td>
                {[0, 1, 2].map((i) => {
                  const score = rs[i];
                  const isDropped = i === droppedIdx;
                  return (
                    <td
                      key={i}
                      className={`mono tnum text-right ${score == null || isDropped ? 'text-3' : 'text-1'}`}
                      style={{ textDecoration: isDropped ? 'line-through' : undefined }}
                      title={isDropped ? 'Lowest round — dropped from final score' : undefined}
                    >
                      {score == null ? '—' : score}
                    </td>
                  );
                })}
                <td className={`mono tnum text-right ${Number.isNaN(r.weightKg) ? 'text-3' : 'text-1'}`}>
                  {Number.isNaN(r.weightKg) ? '—' : `${r.weightKg} kg`}
                </td>
                {/* Final score (avg of best 2) drives ranking — green like the other standings. */}
                <td className="mono tnum text-right text-ok fw-bold">
                  {r.finalScore > 0 ? r.finalScore.toFixed(1) : '—'}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-2 p-7">
                No EC-registered teams.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="border-t txt-11 text-2" style={{ padding: '10px 18px' }}>
        <span className="mono">
          SCORE = AVG OF BEST 2 OF 3 ROUND SCORES · LOWEST ROUND DROPPED · TIES BROKEN BY WEIGHT
        </span>
      </div>
    </div>
  );
}
